import Alert from '@elementor/ui/Alert';
import AlertTitle from '@elementor/ui/AlertTitle';
import Box from '@elementor/ui/Box';
import Button from '@elementor/ui/Button';
import Card from '@elementor/ui/Card';
import CardContent from '@elementor/ui/CardContent';
import Paper from '@elementor/ui/Paper';
import Skeleton from '@elementor/ui/Skeleton';
import Typography from '@elementor/ui/Typography';
import { styled } from '@elementor/ui/styles';
import { ColorPickerStyles } from '@styles/react-colourful.styles';

export const StyledAppContainer = styled(Box)`
	display: flex;
`;

export const StyledContentArea = styled(Box)`
	height: 100vh;
	width: 100%;

	display: flex;
	justify-content: center;
	align-items: center;

	margin-inline-end: 420px;
`;

export const StyledScannerContainer = styled(Paper)`
	position: relative;
	width: 420px;
	height: 100vh;
	max-height: 100vh;
	overflow-y: auto;
	pointer-events: auto;

	* {
		font-family:
			'Roboto Variable', Roboto, Helvetica, Arial, sans-serif !important;
	}

	// Include color picker styles to styled components for prevent problem with cache
	${ColorPickerStyles}
`;

export const StyledMessage = styled(Box)`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;

	svg {
		margin-bottom: ${({ theme, type }) =>
			'loading' === type ? theme.spacing(4) : theme.spacing(3)};
	}

	h1 {
		margin-bottom: ${({ theme }) => theme.spacing(1)};
		color: ${({ theme }) => theme.palette.text.primary};
		font-size: 24px;
		font-weight: 700;
		line-height: 32px;
	}

	p {
		max-width: 330px;

		text-align: center;
		color: ${({ theme }) => theme.palette.text.secondary};
	}
`;

export const StyledStatsBlock = styled(Card)`
	margin: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(2)}`};
	margin-top: ${({ theme }) => theme.spacing(4)};
	padding: ${({ theme }) => theme.spacing(2)};
	padding-bottom: ${({ theme }) => theme.spacing(2.5)};

	border-radius: ${({ theme }) => theme.shape.borderRadius * 2}px;
	border: 1px solid ${({ theme }) => theme.palette.divider};
	background: ${({ theme }) => theme.palette.background.default};
	box-shadow: 0 1px 8px 0 ${({ theme }) => theme.palette.divider};
`;

export const StyledHeaderWrapper = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'isScrolled',
})`
	position: sticky;
	top: 0;
	z-index: 3;

	overflow: visible;
	background-color: ${({ theme }) => theme.palette.background.default};
	box-shadow: ${({ isScrolled }) =>
		isScrolled ? '3px 0px 7px 1px #bbb' : 'none'};
	transition: box-shadow 300ms ease;
`;

export const StyledTitleBox = styled(Box)`
	display: flex;
	justify-content: space-between;
	align-items: center;

	gap: ${({ theme }) => theme.spacing(1)};
	margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

export const StyledTitle = styled(Typography)`
	margin: 0;

	color: ${({ theme }) => theme.palette.common.white};
	font-size: 16px;
	font-weight: 500;
	line-height: 130%;
	letter-spacing: 0.15px;

	.MuiChip-root {
		margin-inline-start: ${({ theme }) => theme.spacing(1)};

		font-weight: 400;
	}
`;

export const StyledHeaderContent = styled(Box)`
	padding: ${({ theme }) => `${theme.spacing(1.75)} ${theme.spacing(2)}`};
	padding-inline-end: ${({ theme }) => theme.spacing(1)};

	background: linear-gradient(-71deg, #2847f3 26.68%, #17298d 124.48%);
	filter: drop-shadow(0 2px 20px rgba(29, 78, 216, 0.4));
	border-radius: 0 0 13px 13px;

	&::after {
		content: '';

		width: 100%;
		height: 30px;

		position: absolute;
		bottom: -6px;
		left: 0;
		right: 0;
		z-index: -1;

		background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='420' height='30' preserveAspectRatio='xMidYMid slice' viewBox='0 0 420 30'%3E%3Cdefs%3E%3ClinearGradient id='a' x1='270.278' x2='182.002' y1='0' y2='222.457' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%232847f3'/%3E%3Cstop offset='.846' stop-color='%2317298d'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cg filter='url(%23filter0_d_109_17852)' transform='translate(0 -26)'%3E%3Cpath fill='url(%23a)' d='m166.977 52.015-2.954 1.97A12 12 0 0 1 157.366 56H16C7.163 56 0 48.837 0 40V0h420v40c0 8.837-7.164 16-16 16H261.633c-2.369 0-4.685-.701-6.656-2.015l-2.954-1.97A12 12 0 0 0 245.366 50h-71.733c-2.369 0-4.685.701-6.656 2.015'/%3E%3C/g%3E%3C/svg%3E");
		background-size: contain;
		background-repeat: no-repeat;
	}
`;

export const StyledContent = styled(CardContent)`
	padding: 0 ${({ theme }) => theme.spacing(2)};
`;

export const StyledSkeletonContainer = styled(Box)`
	display: flex;
	flex-direction: column;

	gap: ${({ theme }) => theme.spacing(1.5)};
`;

export const StyledSkeleton = styled(Skeleton)`
	transform: scale(1);
`;

export const StyledAlert = styled(Alert)`
	align-items: center;
	/* @noflip */
	direction: ltr;
	& .MuiAlert-icon,
	& .MuiAlert-content {
		padding-top: 0;
		display: flex;
	}
`;

export const StyledAlertTitle = styled(AlertTitle)`
	&.MuiAlertTitle-root {
		font-weight: 400;
		line-height: 143%;
	}
`;

export const StyledResolvedStateContainer = styled(Box)`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;

	gap: ${({ theme }) => theme.spacing(1)};
	padding: ${({ theme }) => theme.spacing(4)} 0;
	margin: 25% auto;

	svg {
		position: relative;
		inset-inline-start: ${({ theme }) => theme.spacing(2.75)};
		margin-bottom: ${({ theme }) => theme.spacing(2.75)};
	}
`;

export const StyledStateContainer = styled(Box)`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;

	gap: ${({ theme }) => theme.spacing(2)};
	padding-top: ${({ theme }) => theme.spacing(12)};
	padding-bottom: ${({ theme }) => theme.spacing(6)};
`;

export const StyledButtonContainer = styled(Paper)`
	width: 100%;

	display: flex;
	justify-content: space-between;
	align-items: center;

	padding: ${({ theme }) => `${theme.spacing(1.75)} ${theme.spacing(2)}`};

	&:hover {
		background-color: ${({ theme }) => theme.palette.action.hover};
	}
`;

export const StyledMainLayoutContent = styled(CardContent)`
	display: flex;
	flex-direction: column;

	gap: ${({ theme }) => theme.spacing(1.5)};
	padding: 0 ${({ theme }) => theme.spacing(2)};
`;

export const StyledButton = styled(Button)`
	font-weight: 400;
	justify-content: start;
	padding: 0;

	&:focus .MuiPaper-root,
	&:focus-visible .MuiPaper-root {
		background-color: ${({ theme }) => theme.palette.action.hover};
	}
`;

export const StyledBlockButtonsBox = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing(1.25)};
`;

export const StyledBox = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing(3)};
`;
